import * as React from 'react';
import { logMetadata as logMetadataWithTakt } from '../utils/logWithTakt';

/**
 * Known StormUI layout components from @amzn/storm-ui-layouts. Each layout
 * renders its root element with a `data-sui-component` attribute whose value
 * is the kebab-cased layout name. A page can only use one layout at a time.
 */
const STORM_LAYOUT_COMPONENTS = [
  'simple-layout',
  'form-layout',
  'dashboard-layout',
  'info-layout',
] as const;

type StormLayoutComponent = (typeof STORM_LAYOUT_COMPONENTS)[number];

const STORM_LAYOUT_SELECTOR = STORM_LAYOUT_COMPONENTS.map(
  (name) => `[data-sui-component="${name}"]`,
).join(',');

const isStormLayoutComponent = (
  value: string | null | undefined,
): value is StormLayoutComponent => {
  return (
    value !== null &&
    value !== undefined &&
    (STORM_LAYOUT_COMPONENTS as readonly string[]).indexOf(value) !== -1
  );
};

const findStormLayoutInDom = (): StormLayoutComponent | null => {
  const element = document.querySelector(STORM_LAYOUT_SELECTOR);
  const value = element ? element.getAttribute('data-sui-component') : null;
  return isStormLayoutComponent(value) ? value : null;
};

const logStormLayoutUsage = (): void => {
  const layout = findStormLayoutInDom();
  logMetadataWithTakt({ stormLayout: layout });
};

/**
 * Probes the DOM for the StormUI layout component rendered by the host
 * application (SimpleLayout, FormLayout, DashboardLayout, or InfoLayout) and
 * logs which one is in use as visit metadata. Logs `null` when no StormUI
 * layout is present at the time of the page `load` event.
 *
 * StormUI layout components tag their root element with
 * `data-sui-component="<kebab-case-name>"`, which this hook inspects.
 *
 * Chrome may finish mounting before the host application renders its body,
 * so the hook waits for the page `load` event (or runs immediately if the
 * document is already fully loaded) to give the host application a chance
 * to commit its layout.
 */
export default (): void => {
  React.useEffect(() => {
    if (document.readyState === 'complete') {
      logStormLayoutUsage();
      return undefined;
    }
    window.addEventListener('load', logStormLayoutUsage, { once: true });
    return () => window.removeEventListener('load', logStormLayoutUsage);
  }, []);
};
